import React, { useState, useEffect, useCallback } from 'react';
import { databases, DATABASE_ID, EVENTS_COLLECTION_ID, Query, Event } from '../config/appwrite';
import EventModal from './EventModal';
import Player from './player';

interface EventProps {
  isAdmin?: boolean;
}

/* ─── Event Card ──────────────────────────────────────────────── */
const EventCard: React.FC<{ event: Event; onClick: () => void }> = ({ event, onClick }) => {
  const thumbnailUrl = `https://i.ytimg.com/vi_webp/${event.youtube_link}/maxresdefault.webp`;
  const fallback =
    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiBmaWxsPSIjMGU0NjUzIiBmaWxsLW9wYWNpdHk9IjAuMTUiLz4KPHBhdGggZD0iTTEzNiA3MkwxODQgOTBMMTM2IDEwOFY3MloiIGZpbGw9IiMwZTQ2NTMiIGZpbGwtb3BhY2l0eT0iMC4zIi8+Cjwvc3ZnPgo=';

  return (
    <div
      onClick={onClick}
      className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-200 cursor-pointer overflow-hidden hover:-translate-y-0.5"
    >
      {/* Thumbnail */}
      <div className="relative w-full pt-[56.25%] bg-[#0e4653]/10">
        <img
          src={thumbnailUrl}
          alt={event.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
          onError={e => { (e.target as HTMLImageElement).src = fallback; }}
        />

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {/* Play button */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 group-hover:scale-110">
            <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8 5v10l8-5-8-5z" />
            </svg>
          </div>
        </div>

        {/* Status badge on thumbnail */}
        <div className="absolute top-3 left-3">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold backdrop-blur-sm ${
            event.is_active
              ? 'bg-emerald-500/90 text-white'
              : 'bg-black/50 text-white/80'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${event.is_active ? 'bg-white animate-pulse' : 'bg-white/50'}`} />
            {event.is_active ? 'Live' : 'Inactive'}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3
          className="text-sm font-semibold text-gray-900 mb-2 leading-snug overflow-hidden"
          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
        >
          {event.name}
        </h3>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400 font-mono truncate max-w-[120px]" title={event.youtube_link}>
            {event.youtube_link}
          </span>
          <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
            {new Date(event.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
        </div>

        {/* Manage hint */}
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-[#0e4653]/60 font-medium">Click to manage</span>
          <svg className="w-4 h-4 text-[#0e4653]/40 group-hover:text-[#0e4653] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

/* ─── Empty State ─────────────────────────────────────────────── */
const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-14 bg-white/5 border border-white/10 rounded-2xl">
    <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mb-3">
      <svg className="w-7 h-7 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
      </svg>
    </div>
    <p className="text-white/50 text-sm">{message}</p>
  </div>
);

/* ─── Main component ──────────────────────────────────────────── */
const EventComponent: React.FC<EventProps> = ({ isAdmin = false }) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [activeEvent, setActiveEvent] = useState<Event | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [showInactiveEvents, setShowInactiveEvents] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await databases.listDocuments(DATABASE_ID, EVENTS_COLLECTION_ID, [
        Query.orderDesc('created_at'),
        Query.limit(10000),
      ]);
      const data = response.documents.map(doc => ({
        id: doc.$id,
        name: doc.name as string,
        youtube_link: doc.youtube_link as string,
        is_active: doc.is_active as boolean,
        created_at: doc.created_at as string,
      }));
      setEvents(data);
      const active = data.find((e: Event) => e.is_active);
      if (active) sessionStorage.setItem('activeEventId', active.id);
      setActiveEvent(active || null);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  useEffect(() => {
    const handleEventUpdate = () => {
      if (sessionStorage.getItem('eventUpdate') === 'true') {
        sessionStorage.removeItem('eventUpdate');
        fetchEvents();
      }
    };
    const interval = setInterval(handleEventUpdate, 1000);
    return () => clearInterval(interval);
  }, [fetchEvents]);

  const handleUpdateEvent = useCallback(
    async (eventId: string, updates: Partial<Event>) => {
      try {
        if (eventId === 'all') {
          const response = await databases.listDocuments(DATABASE_ID, EVENTS_COLLECTION_ID, [
            Query.equal('is_active', true),
          ]);
          for (const doc of response.documents) {
            await databases.updateDocument(DATABASE_ID, EVENTS_COLLECTION_ID, doc.$id, { is_active: false });
          }
        } else {
          await databases.updateDocument(DATABASE_ID, EVENTS_COLLECTION_ID, eventId, updates);
          sessionStorage.setItem('activeEventId', eventId);
        }
        if (selectedEvent && selectedEvent.id === eventId) {
          setSelectedEvent({ ...selectedEvent, ...updates });
        }
        await fetchEvents();
      } catch (error) {
        console.error('Error updating event:', error);
      }
    },
    [fetchEvents, selectedEvent]
  );

  const handleDeleteEvent = useCallback(
    async (eventId: string) => {
      try {
        await databases.deleteDocument(DATABASE_ID, EVENTS_COLLECTION_ID, eventId);
        if (sessionStorage.getItem('activeEventId') === eventId) {
          sessionStorage.removeItem('activeEventId');
          window.location.reload();
        }
        fetchEvents();
      } catch (error) {
        console.error('Error deleting event:', error);
      }
    },
    [fetchEvents]
  );

  const handleEventClick = (event: Event) => { setSelectedEvent(event); setIsEventModalOpen(true); };
  const closeEventModal = () => { setIsEventModalOpen(false); setSelectedEvent(null); };

  const activeEvents = events.filter(e => e.is_active);
  const inactiveEvents = events.filter(e => !e.is_active);

  /* ── No active event (regular user) ── */
  if (!isAdmin && !activeEvent) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-white/80 mb-1">No active event</h2>
        <p className="text-white/40 text-sm">Check back soon — an event will be live here shortly.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {isAdmin ? (
        <div className="space-y-8">

          {/* ── Active Events ── */}
          <div>
            <div className="flex items-center gap-2 mb-5">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
              <h3 className="text-base font-semibold text-white">Current Active Event</h3>
              <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/25 text-xs font-semibold px-2 py-0.5 rounded-full">
                {activeEvents.length}
              </span>
            </div>

            {activeEvents.length === 0 ? (
              <EmptyState message="No active event — create one to get started" />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {activeEvents.map(event => (
                  <EventCard key={event.id} event={event} onClick={() => handleEventClick(event)} />
                ))}
              </div>
            )}
          </div>

          {/* ── Inactive Events (collapsible) ── */}
          <div>
            <button
              onClick={() => setShowInactiveEvents(p => !p)}
              className="flex items-center gap-2 mb-5 group"
            >
              <span className="w-2.5 h-2.5 rounded-full bg-white/30" />
              <h3 className="text-base font-semibold text-white/70 group-hover:text-white transition-colors">
                Inactive Events
              </h3>
              <span className="bg-white/10 text-white/50 border border-white/15 text-xs font-semibold px-2 py-0.5 rounded-full">
                {inactiveEvents.length}
              </span>
              <svg
                className={`w-4 h-4 text-white/40 group-hover:text-white/70 transition-all ${showInactiveEvents ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showInactiveEvents && (
              inactiveEvents.length === 0 ? (
                <EmptyState message="No inactive events" />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {inactiveEvents.map(event => (
                    <EventCard key={event.id} event={event} onClick={() => handleEventClick(event)} />
                  ))}
                </div>
              )
            )}
          </div>
        </div>

      ) : (
        /* ── Regular user: active event view ── */
        activeEvent && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              {/* Event header */}
              <div className="bg-[#0e4653] px-5 sm:px-7 py-5">
                <h2 className="text-lg sm:text-xl font-semibold text-white">{activeEvent.name}</h2>
              </div>

              {/* Video player */}
              <div className="bg-black">
                <div className="relative w-full pt-[56.25%]">
                  <div className="absolute inset-0">
                    <Player videoId={activeEvent.youtube_link} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      )}

      {/* Event detail modal */}
      <EventModal
        event={selectedEvent}
        isOpen={isEventModalOpen}
        onClose={closeEventModal}
        onUpdateEvent={handleUpdateEvent}
        onDeleteEvent={handleDeleteEvent}
      />
    </div>
  );
};

export default EventComponent;
